import { computed, ref } from 'vue'
import { CHAT_FILE_ACCEPT } from '@/modules/chat-matrix-fast/constants/fileTypes'

export const MAX_FILES = 10

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}

function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/')
}

function isMediaPreviewFile(file: File): boolean {
  return isImageFile(file) || isVideoFile(file)
}

/** Выбор файлов для отправки: лимит, media-first сортировка, add/remove. */
export function useSelectedFiles(maxFiles = MAX_FILES) {
  const selectedFiles = ref<File[]>([])

  const selectedFilesCount = computed(() => selectedFiles.value.length)
  const canAddMoreFiles = computed(() => selectedFiles.value.length < maxFiles)

  const sortedFiles = computed(() => {
    if (selectedFiles.value.length <= 1) {
      return selectedFiles.value
    }

    const images: File[] = []
    const videos: File[] = []
    const otherFiles: File[] = []

    selectedFiles.value.forEach(file => {
      if (isImageFile(file)) {
        images.push(file)
      } else if (isVideoFile(file)) {
        videos.push(file)
      } else {
        otherFiles.push(file)
      }
    })

    return [...images, ...videos, ...otherFiles]
  })

  const removeFile = (index: number): File | undefined => {
    const [removed] = selectedFiles.value.splice(index, 1)

    return removed
  }

  const removeFileFromSorted = (sortedIndex: number): File | undefined => {
    const fileToRemove = sortedFiles.value[sortedIndex]
    const originalIndex = selectedFiles.value.indexOf(fileToRemove)
    if (originalIndex !== -1) {
      return removeFile(originalIndex)
    }
  }

  const addFiles = (onSelected?: () => void) => {
    const input = document.createElement('input')

    input.type = 'file'
    input.multiple = true
    input.accept = CHAT_FILE_ACCEPT

    input.onchange = e => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      const remainingSlots = maxFiles - selectedFiles.value.length

      if (files.length > remainingSlots) {
        return
      }

      if (files.length > 0) {
        selectedFiles.value.push(...files)
        onSelected?.()
      }
    }

    input.click()
  }

  const setFiles = (files: File[]) => {
    selectedFiles.value = [...files]
  }

  const resetFiles = () => {
    selectedFiles.value = []
  }

  return {
    selectedFiles,
    selectedFilesCount,
    canAddMoreFiles,
    sortedFiles,
    addFiles,
    removeFile,
    removeFileFromSorted,
    setFiles,
    resetFiles,
    isImageFile,
    isVideoFile,
    isMediaPreviewFile,
  }
}
